import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { register } from "../../../services/operations/authAPI";
import toast from "react-hot-toast";
import { getSchools } from "../../../services/operations/schoolAPI";
import { useTranslation } from "react-i18next";
import { FiEye, FiEyeOff } from "react-icons/fi";

const UserAddForm = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const [schools, setSchools] = useState([]);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [form, setForm] = useState({
    name: "",
    email: "",
    phoneno: "",
    role: "",
    schoolId: "",
    password: "",
    confirmPassword: "",
  });

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const validatePassword = (password) => {
    const isValidLength = password.length >= 12;
    const hasUppercase = /[A-Z]/.test(password);
    const hasLowercase = /[a-z]/.test(password);
    const hasNumber = /\d/.test(password);
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);
    return (
      isValidLength &&
      hasUppercase &&
      hasLowercase &&
      hasNumber &&
      hasSpecialChar
    );
  };

  // -------------------------------
  // FETCH SCHOOL LIST
  // -------------------------------
  useEffect(() => {
    const loadSchools = async () => {
      const list = await getSchools();
      if (list) setSchools(list);
    };
    loadSchools();
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!validatePassword(form.password)) {
      toast.error(t("addUserForm.passwordError"));
      return;
    }

    // If role is not super_admin → school is required
    if (form.role !== "super_admin" && !form.schoolId) {
      toast.error("Please select a school");
      return;
    }

    dispatch(
      register(
        form.name,
        form.email,
        form.password,
        form.confirmPassword,
        form.role,
        form.schoolId ,
        form.phoneno,
        navigate
      )
    );
  };

  return (
    <div className="min-h-screen bg-white px-6 pt-4 pb-6 flex flex-col gap-6">
      {/* Header */}
      <div>
        <h2 className="text-xl font-semibold text-blue-900">
          {t("addUserForm.addNewUser")}
        </h2>
        <p className="text-gray-500 text-md font-semibold mt-1">
          {t("addUserForm.createNewUser")}
        </p>
        <div className="h-[2px] w-full bg-blue-500 mt-2" />
      </div>

      <div className="flex flex-col md:flex-row gap-10 justify-center items-center">
        <div className="bg-gray-100 rounded-xl shadow-md p-8 w-full max-w-md">
          <form className="flex flex-col gap-4" onSubmit={handleSubmit}>
            <div>
              <label className="text-sm font-semibold text-gray-700">
                {t("addUserForm.name")}
              </label>
              <input
                name="name"
                value={form.name}
                onChange={handleChange}
                className="mt-1 w-full px-4 py-2 border rounded-md"
                type="text"
                placeholder="Enter username"
              />
            </div>

            <div>
              <label className="text-sm font-semibold text-gray-700">
                {t("addUserForm.email")}
              </label>
              <input
                name="email"
                value={form.email}
                onChange={handleChange}
                className="mt-1 w-full px-4 py-2 border rounded-md"
                type="email"
                placeholder="Enter email"
              />
            </div>

            {/* ✅ Phone Number Field */}
            <div>
              <label className="text-sm font-semibold text-gray-700">
                Phone Number (Optional)
              </label>
              <input
                name="phoneno"
                value={form.phoneno}
                onChange={handleChange}
                className="mt-1 w-full px-4 py-2 border rounded-md"
                type="tel"
                placeholder="Enter phone number"
              />
            </div>

            <div>
              <label className="text-sm font-semibold text-gray-700">
                {t("addUserForm.role")}
              </label>
              <select
                name="role"
                value={form.role}
                onChange={handleChange}
                className="mt-1 w-full px-4 py-2 border rounded-md bg-white"
              >
                <option value="" disabled>
                  {t("addUserForm.selectRole")}
                </option>
                {/* <option value="student">{t("addUserForm.student")}</option>
                <option value="teacher">{t("addUserForm.teacher")}</option>
                <option value="parent">{t("addUserForm.parent")}</option> */}
                <option value="school_admin">
                  {t("addUserForm.schoolAdmin")}
                </option>
                <option value="super_admin">
                  {t("addUserForm.superAdmin")}
                </option>
              </select>
            </div>

            {/* SHOW SCHOOL SELECT IF ROLE != super_admin */}
            {form.role && form.role !== "super_admin" && (
              <div>
                <label className="text-sm font-semibold text-gray-700">
                  Select School
                </label>

                <select
                  name="schoolId"
                  value={form.schoolId}
                  onChange={handleChange}
                  className="mt-1 w-full px-4 py-2 border rounded-md bg-white"
                >
                  <option value="" disabled>
                    -- Select School --
                  </option>

                  {schools.map((school) => (
                    <option key={school._id} value={school._id}>
                      {school.name}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Password */}
            <div className="relative">
              <label className="text-sm font-semibold text-gray-700">
                {t("addUserForm.password")}
              </label>
              <input
                name="password"
                value={form.password}
                onChange={handleChange}
                className="mt-1 w-full px-4 py-2 border rounded-md"
                type={showPassword ? "text" : "password"}
                placeholder={t("addUserForm.enterPassword")}
              />
              <button
                type="button"
                onClick={() => setShowPassword((prev) => !prev)}
                className="absolute mt-3 right-3 top-1/2 transform -translate-y-1/2 text-gray-600"
              >
                {showPassword ? <FiEyeOff size={20} /> : <FiEye size={20} />}
              </button>
            </div>

            {/* Confirm Password */}
            <div className="relative">
              <label className="text-sm font-semibold text-gray-700">
                {t("addUserForm.confirmPassword")}
              </label>
              <input
                name="confirmPassword"
                value={form.confirmPassword}
                onChange={handleChange}
                className="mt-1 w-full px-4 py-2 border rounded-md"
                type={showConfirmPassword ? "text" : "password"}
                placeholder={t("addUserForm.confirmPasswordPlaceholder")}
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword((prev) => !prev)}
                className="absolute mt-3 right-3 top-1/2 transform -translate-y-1/2 text-gray-600"
              >
                {showConfirmPassword ? (
                  <FiEyeOff size={20} />
                ) : (
                  <FiEye size={20} />
                )}
              </button>
            </div>

            <div className="flex justify-end gap-4 pt-2">
              <button
                type="button"
                onClick={() => navigate("/dashboard/super-admin/users")}
                className="border border-blue-900 text-blue-900 px-5 py-2 rounded-full text-sm font-medium hover:bg-blue-50"
              >
                {t("addUserForm.cancel")}
              </button>

              <button
                type="submit"
                className="bg-blue-900 text-white px-6 py-2 rounded-full text-sm font-medium hover:bg-blue-800"
              >
                {t("addUserForm.add")}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default UserAddForm;
